<template>
  <ListViewBanner :add-route="{ name: 'app_category_new', params: { framework: route.params.framework } }">
    <template v-if="loading.framework">
      Loading...
    </template>

    <template v-else>
      <div class="flex items-center">
        <img
          :src="framework.icon"
          :alt="`${framework.name} icon`"
          class="w-auto h-10"
        >

        <span class="ms-3">{{ `${framework.name} Categories` }}</span>
      </div>
    </template>
  </ListViewBanner>

  <CategorySearch @search="searchParams = $event" />

  <AppContainer>
    <AppTable>
      <AppTableHeader>
        <th class="w-24">
          Icon
        </th>
        <th>Name</th>
        <th class="text-right">
          Sort Order
        </th>
        <th>
          <span class="sr-only">Actions</span>
        </th>
      </AppTableHeader>

      <AppTableBody
        :loading="loading.categories"
        :no-results="!categories.length"
        :colspan="3"
      >
        <template v-if="categories.length">
          <tr
            v-for="category in categories"
            :key="category.id"
            class="overflow-visible"
          >
            <td>
              <CategoryIcon
                :name="category.name"
                :background-color="category.backgroundColor"
                :icon="category.icon"
              />
            </td>

            <td>
              {{ category.shortName }}

              <template v-if="category.archived">
                <BadgeArchived />
              </template>
            </td>

            <td class="text-right">
              {{ category?.sortOrder }}
            </td>

            <td>
              <div class="flex items-center justify-end">
                <template v-if="category.archived">
                  <RestoreButton @click="categoryToRestore = category" />
                </template>

                <template v-else>
                  <AppDropdown>
                    <AppDropdownItem
                      text="Edit"
                      :to="{ name: 'app_category_edit', params: { framework: route.params.framework, category: category.id } }"
                    />

                    <AppDropdownItem
                      text="Archive"
                      @click="categoryToArchive = category"
                    />
                  </AppDropdown>
                </template>
              </div>
            </td>
          </tr>
        </template>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @page-change="searchParams.page = $event"
      @items-per-page-change="searchParams.itemsPerPage = $event"
    />
  </AppContainer>

  <!-- Archive Modal -->
  <template v-if="categoryToArchive">
    <ConfirmationModal
      title="Archive Category"
      :processing="archiving"
      @confirm="archiveEntity"
      @close="categoryToArchive = false"
    >
      <p>
        <span class="block">Name: {{ categoryToArchive?.name }}</span>
        Are you sure you want to <strong>ARCHIVE</strong> this Category?
      </p>
    </ConfirmationModal>
  </template>

  <!-- Restore Modal -->
  <template v-if="categoryToRestore">
    <ConfirmationModal
      title="Restore Category"
      :processing="restoring"
      @confirm="restoreCategory"
      @close="categoryToRestore = false"
    >
      <p>
        <span class="block">Name: {{ categoryToRestore?.name }}</span>
        Are you sure you want to <strong>RESTORE</strong> this Category?
      </p>
    </ConfirmationModal>
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';

import { AppPagination } from '@/components/app';
import { BadgeArchived } from '@/components/badges';
import { ListViewBanner } from '@/components/banners';
import { CategoryIcon } from '@/components/category';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { CategorySearch } from '@/components/forms/search';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';

import { useHttp, useToasts } from '@/composables';
import { AppContainer } from '@/components/app/index.js';
import { ConfirmationModal } from '@/components/modals/index.js';
import { RestoreButton } from '@/components/buttons/index.js';

const route = useRoute();
const toasts = useToasts();

const loading = ref({ framework: true, categories: true });
const framework = ref();
const categories = ref([]);
let searchParams = ref({});
const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
});

const loadFramework = async () => {
  const response = await useHttp().get(`/frameworks/${route.params.framework}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    framework.value = response.data;
  }

  loading.value.framework = false;
};

const loadCategories = async () => {
  loading.value.categories = true;
  categories.value = [];

  const response = await useHttp().get(`/frameworks/${route.params.framework}/categories`, searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    categories.value = response.data.items;
    paginationData.value = response.data.pagination;
  } else {
    toasts.error('Unable to load the categories for this framework');
  }

  loading.value.categories = false;
};

const categoryToArchive = ref(null);
const archiving = ref(false);
const categoryToRestore = ref(null);
const restoring = ref(false);

const archiveEntity = async () => {
  archiving.value = true;

  const response = await useHttp().post(`/frameworks/${route.params.framework}/categories/${categoryToArchive.value.id}/archive`);

  if (response.statusCode === HttpStatusCode.Ok) {
    toasts.success('Category successfully archived');
    categoryToArchive.value = null;

    await loadCategories();
  } else {
    toasts.error('Could not archive category');
  }

  archiving.value = false;
};

const restoreCategory = async () => {
  restoring.value = true;

  const response = await useHttp().post(`/frameworks/${route.params.framework}/categories/${categoryToRestore.value.id}/restore`);

  if (response.statusCode === HttpStatusCode.Ok) {
    toasts.success('Category successfully restored');
    categoryToRestore.value = null;

    await loadCategories();
  } else {
    toasts.error('Could not restore category');
  }

  restoring.value = false;
};

loadFramework();
watchEffect(loadCategories);
</script>
